import React, { useRef } from 'react';
import { IconType } from 'react-icons';
import InPlaceTextInput from './TextInputs/InPlaceTextInput';

interface TitleEditorProps {
    name: string;
    id: string;
    icon?: IconType;
    titleType?: 'project' | 'image' | 'result';
    onUpdate: (id: string, name: string) => Promise<void>;
    onRefetch?: () => void;
    className?: string;
}

export default function TitleEditor(props: TitleEditorProps) {
    const {
        name,
        id,
        icon: Icon,
        titleType = 'project',
        onUpdate,
        onRefetch,
        className = ''
    } = props;

    const lastModifiedName = useRef(name);
    const [editing, setEditing] = React.useState(false);
    const [currentName, setCurrentName] = React.useState(name);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentName(e.target.value);
    }

    const handleNameBlur = async () => {
        setEditing(false);
        if (currentName !== lastModifiedName.current && currentName.trim() !== '') {
            lastModifiedName.current = currentName;
            setIsLoading(true);
            try {
                await onUpdate(id, currentName);
                onRefetch?.();
            } catch (error: any) {
                console.log(`Failed to update ${titleType}`, error);
                setError(`Unable to save ${titleType} name, please try again later.`);
            } finally {
                setIsLoading(false);
            }
        } else {
            if (currentName.trim() === '') {
                setCurrentName(lastModifiedName.current);
            }
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleNameBlur();
        } else if (e.key === 'Escape') {
            setCurrentName(lastModifiedName.current);
            setEditing(false);
        }
    }

    return (
        <div>
            <h1 className={`text-3xl font-bold ${className}`}>
                {Icon && <Icon className="inline mr-3 mb-1" />}
                {editing ?
                    <InPlaceTextInput
                        id={`${titleType}-title`}
                        label={`${titleType.charAt(0).toUpperCase() + titleType.slice(1)} Title`}
                        value={currentName}
                        autoFocus={true}
                        onChange={handleNameChange}
                        onBlur={handleNameBlur}
                        onFocus={() => setEditing(true)}
                        className='px-2'
                        onKeyDown={handleKeyDown}
                    />
                    : <span className='px-2 hover:bg-base-200 hover:cursor-text rounded-md' onClick={() => setEditing(true)}>{currentName}</span>
                }
                {isLoading && <span className='text-base-content/60 text-sm ml-2'>saving...</span>}
                {error && <span className='text-error text-sm ml-2'>{error}</span>}
            </h1>
        </div>
    )
} 